import React from 'react';
import { faScissors, faList, faHome, faPuzzlePiece, faTag } from '@fortawesome/pro-regular-svg-icons';
import { faVanShuttle, faWhistle, faDogLeashed } from '@fortawesome/pro-light-svg-icons';
import {
    MembershipItem,
    MembershipItemType,
    MEMBERSHIP_ITEM_TYPES,
    MembershipItemTypeValue
} from '@spike/membership-model';
import { MembershipItemsTableItem } from 'components/Memberships/Add/Steps/Items/Table/MembershipItemsTableItem';
import { ItemSelectionDrawer, ItemDisplayConfig } from 'components/UI/V3/ItemSelectionDrawer/ItemSelectionDrawer';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Dropdown } from 'components/UI/V2/Dropdown/Dropdown';
import clsx from 'clsx';
import { useMembershipItemDrawers, DrawerState } from '../hooks/useMembershipItemDrawers';
import { useMembershipItemDisplayConfigs } from '../hooks/useMembershipItemDisplayConfigs';

function renderItemDrawer<T extends { id?: string | number }>(
    key: string,
    title: string,
    selectAllLabel: string,
    searchPlaceholder: string,
    state: DrawerState<T>,
    itemDisplay: ItemDisplayConfig<T>
): React.ReactElement {
    return (
        <ItemSelectionDrawer<T>
            key={key}
            isOpen={state.drawer.isOpen}
            title={title}
            items={state.availableItems}
            selectedItems={state.currentItems}
            tempSelectedItems={state.drawer.tempSelectedItems}
            searchQuery={state.drawer.searchQuery}
            filteredItems={state.drawer.filteredItems}
            selectAllLabel={selectAllLabel}
            searchPlaceholder={searchPlaceholder}
            itemDisplay={itemDisplay}
            onClose={state.drawer.closeDrawer}
            onSearchChange={state.drawer.setSearchQuery}
            onTempSelectionChange={state.drawer.setTempSelectedItems}
            onApply={state.drawer.handleApply}
            onCancel={state.drawer.handleCancel}
        />
    );
}

interface MembershipItemsTableProps {
    items: MembershipItem[];
    onChange: (items: MembershipItem[]) => void;
    excludedItemIDs: string[];
    marketplaceID: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grid: {
            display: 'grid',
            gap: '16px 32px',
            gridTemplateColumns: 'minmax(90px, 1fr) 110px minmax(70px, 1fr)',

            [theme.breakpoints.up('md')]: {
                gap: 16,
                gridTemplateColumns: '1fr 120px 92px 75px'
            }
        },
        header: {
            paddingBottom: 16,
            borderBottom: 'solid 2px #000000',

            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        headerLabel: {
            'fontSize': 15,
            'lineHeight': 1.6,
            'fontWeight': 600,
            'color': '#000000',
            'textAlign': 'center',

            '&:first-child': {
                textAlign: 'left'
            },
            '&:last-child': {
                textAlign: 'right'
            }
        },
        dropdown: {
            display: 'block',
            marginTop: 40,

            [theme.breakpoints.down('sm')]: {
                marginTop: 10
            }
        }
    })
);

export const MembershipItemsTable: React.FC<MembershipItemsTableProps> = props => {
    const classes = useStyles();

    const { openDrawerForType, grooming, daycare, boarding, walking, transportation, training, addons, products } =
        useMembershipItemDrawers({
            items: props.items,
            excludedItemIDs: props.excludedItemIDs,
            marketplaceID: props.marketplaceID,
            onChange: props.onChange
        });

    const { serviceDisplay, addonDisplay, productDisplay } = useMembershipItemDisplayConfigs();

    const changeMembershipItemHandler = (item: MembershipItem) => {
        props.onChange(props.items.map(i => (i.uuid === item.uuid ? item : i)));
    };

    const deleteMembershipItemHandler = (uuid: string) => {
        props.onChange(props.items.filter(i => i.uuid !== uuid));
    };

    const dropdownOptions = React.useMemo(() => {
        const options = [];

        if (grooming.catalogItems.length > 0) {
            options.push({
                icon: faScissors,
                label: 'Grooming Service',
                value: MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE
            });
        }
        if (daycare.catalogItems.length > 0) {
            options.push({ icon: faList, label: 'Daycare Service', value: MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE });
        }
        if (boarding.catalogItems.length > 0) {
            options.push({ icon: faHome, label: 'Boarding Service', value: MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE });
        }
        if (walking.catalogItems.length > 0) {
            options.push({
                icon: faDogLeashed,
                label: 'Walking Service',
                value: MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE
            });
        }
        if (transportation.catalogItems.length > 0) {
            options.push({
                icon: faVanShuttle,
                label: 'Transportation Service',
                value: MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE
            });
        }
        if (training.catalogItems.length > 0) {
            options.push({ icon: faWhistle, label: 'Training Service', value: MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE });
        }
        if (addons.catalogItems.length > 0) {
            options.push({ icon: faPuzzlePiece, label: 'Add-On', value: MEMBERSHIP_ITEM_TYPES.ADD_ON });
        }
        if (products.catalogItems.length > 0) {
            options.push({ icon: faTag, label: 'Product', value: MEMBERSHIP_ITEM_TYPES.PRODUCT });
        }

        return options;
    }, [
        grooming.catalogItems,
        daycare.catalogItems,
        boarding.catalogItems,
        walking.catalogItems,
        transportation.catalogItems,
        training.catalogItems,
        addons.catalogItems,
        products.catalogItems
    ]);

    return (
        <>
            <div>
                <div className={clsx(classes.grid, classes.header)}>
                    <div className={classes.headerLabel}>Item</div>
                    <div className={classes.headerLabel}>Qty</div>
                    <div className={classes.headerLabel}>Price</div>
                    <div className={classes.headerLabel}>Total</div>
                </div>

                <div>
                    {props.items.map(item => (
                        <MembershipItemsTableItem
                            key={item.uuid}
                            item={item}
                            className={classes.grid}
                            onChange={changeMembershipItemHandler}
                            onDelete={deleteMembershipItemHandler}
                            currentItems={props.items}
                            excludedItemIDs={props.excludedItemIDs}
                        />
                    ))}
                </div>

                <Dropdown
                    label="Add Item"
                    options={dropdownOptions}
                    className={classes.dropdown}
                    onSelect={openDrawerForType}
                />
            </div>

            {renderItemDrawer(
                'grooming',
                'Select Grooming Services',
                'All Services',
                'Search by service name',
                grooming,
                serviceDisplay
            )}
            {renderItemDrawer(
                'daycare',
                'Select Daycare Services',
                'All Services',
                'Search by service name',
                daycare,
                serviceDisplay
            )}
            {renderItemDrawer(
                'boarding',
                'Select Boarding Services',
                'All Services',
                'Search by service name',
                boarding,
                serviceDisplay
            )}
            {renderItemDrawer(
                'walking',
                'Select Walking Services',
                'All Services',
                'Search by service name',
                walking,
                serviceDisplay
            )}
            {renderItemDrawer(
                'transportation',
                'Select Transportation Services',
                'All Services',
                'Search by service name',
                transportation,
                serviceDisplay
            )}
            {renderItemDrawer(
                'training',
                'Select Training Services',
                'All Services',
                'Search by service name',
                training,
                serviceDisplay
            )}
            {renderItemDrawer('addons', 'Select Add-ons', 'All Add-ons', 'Search by add-on name', addons, addonDisplay)}
            {renderItemDrawer(
                'products',
                'Select Products',
                'All Products',
                'Search by product name',
                products,
                productDisplay
            )}
        </>
    );
};
